import React, { useCallback, useMemo, useState } from 'react'

import {
  ChevronRightIcon,
  DeliveryIcon,
  PickUpIcon,
  ScheduleIcon
} from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'
import startCase from 'lodash/startCase'

import ToastProduct from 'src/public/components/online_ordering/ToastProduct'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import {
  formatTimeString,
  FullSchedule,
  getCurrentScheduleInterval,
  getNextScheduleInterval,
  getToday,
  getTomorrow,
  mergeSchedules
} from 'src/lib/js/schedule'
import { t } from 'src/translations'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import Toggle from 'shared/components/common/toggle/Toggle'

import AllHoursModal from 'public/components/default_template/footer/location/AllHoursModal'
import { useIsDarkMode } from 'public/components/default_template/meta/useTheme'
import FulfillmentTimeSummary from 'public/components/default_template/online_ordering/dining_behavior_toggle/FulfillmentTimeSummary'
import {
  useFulfillment,
  useLocationSchedules
} from 'public/components/online_ordering/FulfillmentContext'
import {
  trackDiningOptionChangedV2,
  TrackingSource
} from 'src/public/components/default_template/online_ordering/dining_behavior_toggle/fulfillmentTrackingUtils'
import DiningBehaviorSummary from 'public/components/default_template/online_ordering/dining_behavior_toggle/DiningBehaviorSummary'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import useTracker from 'src/lib/js/hooks/useTracker'
import ClosingTimeAlert from 'public/components/default_template/online_ordering/dining_behavior_toggle/ClosingTimeAlert'

const FulfillmentPanel = () => {
  const { canOrderTakeout, canOrderDelivery } = useFulfillment()
  const { toastProduct } = useRestaurant()
  const darkMode = useIsDarkMode()
  const isToastLocal = useMemo(
    () =>
      toastProduct === ToastProduct.ToastLocal ||
      toastProduct === ToastProduct.FreeOOToastLocal ||
      toastProduct === ToastProduct.ToastApp,
    [toastProduct]
  )

  const oneDiningOption = canOrderTakeout !== canOrderDelivery
  const twoDiningOptions = canOrderDelivery && canOrderTakeout

  return (
    <div
      data-testid='fulfillment-panel'
      className={classnames('fulfillmentPanel', isToastLocal && 'toastLocal')}
    >
      <div className='fulfillmentToggle'>
        {twoDiningOptions ? (
          <FulfillmentToggle
            darkMode={darkMode}
            trackingSource={TrackingSource.Menu}
          />
        ) : oneDiningOption ? (
          <SingleDiningOptionPill darkMode={darkMode} />
        ) : (
          <NotAcceptingOrdersPill />
        )}
      </div>
      <div className='fulfillmentSummaries'>
        <DiningBehaviorSummary />
        <FulfillmentTimeSummary />
        <ClosingTimeAlert />
      </div>
    </div>
  )
}

const FulfillmentToggle = ({
  darkMode,
  trackingSource
}: {
  darkMode: boolean
  trackingSource: TrackingSource
}) => {
  const {
    selectedDiningOptionBehavior,
    setSelectedDiningOptionBehavior,
    updateCartFulfillment,
    fulfillmentData,
    canOrderTakeout,
    canOrderDelivery
  } = useFulfillment()
  const tracker = useTracker()
  const [isToggling, setIsToggling] = useState(false)
  const { lastKnownValidDeliveryAddress } = useDelivery()
  const toggleValue = useMemo(
    () =>
      selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
        ? 'left'
        : 'right',
    [selectedDiningOptionBehavior]
  )
  const onToggle = useCallback(
    async (value: DiningOptionBehavior) => {
      if (isToggling) return

      setIsToggling(true)
      try {
        setSelectedDiningOptionBehavior(value)

        if (fulfillmentData?.cartFulfillmentData) {
          // TODO: Here we lose past state if the user is click happy between fulfillent modes.
          // Maybe here we look for a "last used delivery address" from memory or session storage if it exists?
          // optimize this once we're happy with integral code paths.
          await updateCartFulfillment({
            fulfillmentType:
              fulfillmentData.cartFulfillmentData.fulfillmentType,
            fulfillmentDateTime:
              fulfillmentData.cartFulfillmentData.fulfillmentDateTime,
            diningOptionBehavior: value,
            deliveryInfo:
              value === DiningOptionBehavior.Delivery
                ? lastKnownValidDeliveryAddress ??
                  fulfillmentData.cartFulfillmentData.deliveryInfo ??
                  null
                : null
          })
          trackDiningOptionChangedV2(
            tracker,
            fulfillmentData.cartFulfillmentData.fulfillmentType,
            false,
            trackingSource,
            value,
            lastKnownValidDeliveryAddress
          )
        }
      } finally {
        setIsToggling(false)
      }
    },
    [
      setSelectedDiningOptionBehavior,
      updateCartFulfillment,
      tracker,
      trackingSource,
      fulfillmentData,
      isToggling,
      lastKnownValidDeliveryAddress
    ]
  )

  return (
    <Toggle
      className={darkMode ? 'darkMode' : undefined}
      testId='fulfillment-toggle'
      value={toggleValue}
      onChange={onToggle}
      themed
      left={{
        name: t('fulfillment-general.delivery'),
        value: DiningOptionBehavior.Delivery,
        disabled: !canOrderDelivery || isToggling,
        icon: <DeliveryIcon accessibility='decorative' />,
        dataTestId: 'toggle-delivery'
      }}
      right={{
        name: t('fulfillment-general.pickup'),
        value: DiningOptionBehavior.TakeOut,
        disabled: !canOrderTakeout || isToggling,
        icon: <PickUpIcon accessibility='decorative' />,
        dataTestId: 'toggle-pickup'
      }}
    />
  )
}

const SingleDiningOptionPill = ({ darkMode }: { darkMode: boolean }) => {
  const { selectedDiningOptionBehavior } = useFulfillment()
  const icon = useMemo(
    () =>
      selectedDiningOptionBehavior === DiningOptionBehavior.Delivery ? (
        <DeliveryIcon accessibility='decorative' />
      ) : (
        <PickUpIcon accessibility='decorative' />
      ),
    [selectedDiningOptionBehavior]
  )

  return (
    <div
      className={classnames(
        'singleFulfillmentPill',
        darkMode ? 'darkMode' : undefined
      )}
      data-testid='fulfillment-pill'
    >
      {icon}
      <span>{`${
        selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
          ? t('fulfillment-general.delivery')
          : t('fulfillment-general.pickup')
      } ${t('fulfillment-panel.only')}`}</span>
    </div>
  )
}

const NotAcceptingOrdersPill = () => {
  const { restaurant, ooRestaurant } = useRestaurant()
  const { selectedDiningOptionBehavior } = useFulfillment()
  const location = restaurant.locations?.find(
    (loc) => loc.id === ooRestaurant?.guid
  )
  const locationSchedules = useLocationSchedules()
  const locationSchedule = ooRestaurant?.guid
    ? locationSchedules[ooRestaurant.guid]
    : undefined
  const timezoneId = useMemo(
    () => locationSchedule?.timeZoneId ?? 'America/New_York',
    [locationSchedule?.timeZoneId]
  )
  const mergedSchedules: FullSchedule | undefined = useMemo(() => {
    if (!location) return undefined
    return mergeSchedules(
      location,
      locationSchedule,
      !!restaurant.config.isOnlineOrderingOnly,
      selectedDiningOptionBehavior ?? undefined
    )
  }, [
    selectedDiningOptionBehavior,
    location,
    locationSchedule,
    restaurant.config.isOnlineOrderingOnly
  ])

  const isLocationOpen = useMemo(() => {
    if (mergedSchedules) {
      return !!getCurrentScheduleInterval(mergedSchedules, timezoneId)
    }
    return false
  }, [mergedSchedules, timezoneId])

  const nextInterval = mergedSchedules
    ? getNextScheduleInterval(mergedSchedules, timezoneId)
    : undefined

  if (!isLocationOpen && nextInterval && mergedSchedules) {
    const today = getToday(timezoneId)
    const tomorrow = getTomorrow(timezoneId)
    const timeString = formatTimeString(nextInterval.interval.startTime)
    const nextEventString =
      nextInterval.day === today
        ? t('fulfillment-panel.opens-at', { time: timeString })
        : nextInterval.day === tomorrow
        ? t('fulfillment-panel.opens-tomorrow', { time: timeString })
        : t('fulfillment-panel.opens-day', {
            day: startCase(nextInterval.day),
            time: timeString
          })
    return (
      <AllHoursModal
        restaurantName={location?.name || restaurant.name}
        isLocationOpen={isLocationOpen}
        schedule={mergedSchedules}
        timezoneId={timezoneId}
      >
        {(open) => (
          <button
            className='notAcceptingOrdersPill'
            data-testid='not-accepting-orders-pill'
            onClick={open}
          >
            <div className='content'>
              <ScheduleIcon size='xs' accessibility='decorative' />
              <span>{nextEventString}</span>
            </div>
            <ChevronRightIcon
              size='xs'
              accessibility='semantic'
              aria-label={t('fulfillment-panel.see-sub-menus')}
              className='chevronIcon'
            />
          </button>
        )}
      </AllHoursModal>
    )
  } else {
    return null
  }
}

export default FulfillmentPanel
